// Apolo IDE — Plan próximos-passos heuristics (sprint plan-suggestions).
//
// Função PURA (sem React, sem efeitos) que olha os blocks do canvas e sugere
// o que falta. Detecta lacunas das 6 fases canônicas e propõe heading + skill
// recomendada. Testável isoladamente.

// Fases canônicas, em ordem de aparição num plano, com a skill recomendada.
export const PHASES = [
  { id: 'discovery',    label: 'Discovery',   emoji: '🔍', icon: 'Compass',      skill: 'brainstorming',      synonyms: ['discovery', 'descoberta', 'pesquisa'] },
  { id: 'architecture', label: 'Arquitetura', emoji: '🏗', icon: 'Layers',       skill: 'architect-reviewer', synonyms: ['architecture', 'arquitetura', 'arquitet'] },
  { id: 'database',     label: 'Database',    emoji: '🗃', icon: 'Database',     skill: 'sql-pro',            synonyms: ['database', 'banco', 'dados', 'db ', 'schema'] },
  { id: 'delivery',     label: 'Delivery',    emoji: '🚀', icon: 'Rocket',       skill: 'sprint-planning',    synonyms: ['delivery', 'entrega', 'sprint', 'deploy', 'roadmap'] },
  { id: 'security',     label: 'Segurança',   emoji: '🔒', icon: 'Shield',       skill: 'security-review',    synonyms: ['security', 'segurança', 'seguranca', 'cyber'] },
  { id: 'qa',           label: 'QA & Testes', emoji: '🧪', icon: 'FlaskConical', skill: 'qa-expert',          synonyms: ['qa', 'teste', 'test', 'quality'] },
];

// skill_name → phase (catálogo planSkillsCatalog.js). Usado quando o skill
// block não carrega _phase no payload.
export const SKILL_PHASE = {
  brainstorming: 'discovery', 'ux-researcher': 'discovery', 'pedro-brain': 'discovery', 'competitive-analyst': 'discovery',
  'architect-reviewer': 'architecture', 'system-design': 'architecture', 'microservices-architect': 'architecture', 'api-designer': 'architecture',
  'sql-pro': 'database', 'database-optimizer': 'database', 'postgres-pro': 'database', 'data-engineer': 'database',
  'sprint-planning': 'delivery', 'sprint-directive-prompt': 'delivery', 'subagent-driven-development': 'delivery', tdd: 'delivery', 'deployment-engineer': 'delivery',
  'security-review': 'security', 'penetration-tester': 'security', 'compliance-auditor': 'security', 'incident-responder': 'security',
  'qa-expert': 'qa', 'test-automator': 'qa',
};

const PHASE_BY_ID = Object.fromEntries(PHASES.map((p) => [p.id, p]));

/**
 * Conjunto de fases já presentes no canvas. Detecta via skill blocks
 * (_phase no payload ou mapa skill→phase) e via heading que cita a fase.
 */
export function detectPhases(blocks) {
  const present = new Set();
  for (const b of Array.isArray(blocks) ? blocks : []) {
    if (!b) continue;
    if (b.kind === 'skill') {
      const ph = b.payload?._phase || SKILL_PHASE[b.payload?.skill_name];
      if (ph && PHASE_BY_ID[ph]) present.add(ph);
    } else if (b.kind === 'heading') {
      const txt = String(b.payload?.text || '').toLowerCase();
      for (const p of PHASES) {
        if (p.synonyms.some((s) => txt.includes(s))) present.add(p.id);
      }
    }
  }
  return present;
}

/**
 * Lista de sugestões heurísticas (chips). Cada sugestão tem `actions` — os
 * blocos a criar quando o chip é clicado.
 *
 * @param {Array} blocks
 * @returns {Array<{id,label,icon,reason,actions:Array<{kind,payload}>}>}
 */
export function computeSuggestions(blocks) {
  const list = Array.isArray(blocks) ? blocks : [];

  // Canvas vazio/quase vazio → oferece estrutura base completa.
  if (list.length <= 1) {
    return [{
      id: 'scaffold',
      label: 'Gerar estrutura base',
      icon: 'LayoutTemplate',
      reason: 'Cria os títulos das 6 fases (discovery → segurança) pra você começar.',
      actions: PHASES.map((p) => ({ kind: 'heading', payload: { text: `${p.emoji} ${p.label}`, level: 2 } })),
    }];
  }

  const present = detectPhases(list);
  const missing = PHASES.filter((p) => !present.has(p.id)).slice(0, 4);
  return missing.map((p) => ({
    id: `phase-${p.id}`,
    label: p.label,
    icon: p.icon,
    reason: `Seu plano ainda não tem ${p.label.toLowerCase()}. Adiciona um título da fase + a skill ${p.skill}.`,
    actions: [
      { kind: 'heading', payload: { text: `${p.emoji} ${p.label}`, level: 2 } },
      { kind: 'skill', payload: { skill_name: p.skill, version: 'latest', notes: '', _phase: p.id } },
    ],
  }));
}
